import _lowerCase from "lodash/lowerCase";
import _replace from "lodash/replace";
import _capitalize from "lodash/capitalize";
import _isNil from "lodash/isNil";
import "core-js/modules/es6.regexp.split.js";
import "core-js/modules/es6.object.to-string.js";
import "core-js/modules/es6.regexp.to-string.js";
import "core-js/modules/es6.regexp.replace.js";
import "core-js/modules/es6.number.is-nan.js";
import "core-js/modules/es6.number.constructor.js";
import currency from 'currency.js';
import { FORMATS } from '../constants/drivers';
/**
 * Get the number of decimals that the given number has.
 * E.g.
 *  0.005 => 3.
 *  1997 => 0.
 *  1.23 => 2.
 * @param {Number} value
 * @returns {Number} the number of decimal places the given value has.
 */

var countDecimals = function countDecimals(value) {
  if (Math.floor(value.valueOf()) === value.valueOf()) {
    return 0;
  }

  return value.toString().split('.')[1].length || 0;
};
/**
 *
 * @param {Currency} c The currency.js object
 * @param {*} settings The currency.js settings merged with it's default settings.
 * @returns {String} the formatted currency.
 */


var fractionalFormatter = function fractionalFormatter(c, settings) {
  var pattern = settings.pattern,
      negativePattern = settings.negativePattern,
      symbol = settings.symbol,
      separator = settings.separator,
      decimal = settings.decimal,
      groups = settings.groups;
  var split = "".concat(c).replace(/^-/, '').split('.');
  var dollars = split[0];
  var valuePrecision = countDecimals(c.value);
  var currencyCents = c.cents();
  var cents = '';

  if (valuePrecision < 3) {
    return currency(c.value, {
      pattern: pattern,
      negativePattern: negativePattern,
      symbol: symbol,
      decimal: decimal,
      groups: groups,
      precision: 2
    }).format();
  }

  if (currencyCents % 10 !== 0) {
    cents = split === null || split === void 0 ? void 0 : split[1];
  } else if (currencyCents >= 100) {
    cents = "".concat(currencyCents).substring(0, 2);
  } else {
    cents = "0".concat(currencyCents).substring(0, 3);
  }

  return (c.value >= 0 ? pattern : negativePattern).replace('!', symbol).replace('#', dollars.replace(groups, "$1".concat(separator)) + (cents ? decimal + cents : ''));
};
/**
 * Format a string from snake case to sentence case
 * (e.g. 'average_set_of_discounts' -> 'Average set of discounts').
 *
 * @param {string} value - The string value to be formatted.
 * @return {string} - Formatted string in sentence case.
 */


export var formatSnakeCase = function formatSnakeCase(value) {
  if (_isNil(value) || value === '') {
    return null;
  }

  return _capitalize(_replace(_lowerCase(value), /_/g, ' '));
};
/**
 * Driver value formatter.
 *
 * @param {string} value - The string value to be formatted.
 * @param {format} [format] - The format type.
 * @param {boolean} shouldDisplayEmptyAsNA - If true, will return 'N/A' for empty values
 * @return {string} - Formatted string.
 */

export var formatValue = function formatValue(value, format) {
  var shouldDisplayEmptyAsNA = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

  if (_isNil(value) || value === '') {
    if (shouldDisplayEmptyAsNA) {
      return 'N/A';
    }

    return null;
  }

  var curr = currency(value);

  switch (format) {
    case FORMATS.CURRENCY:
      return currency(value, {
        precision: 0
      }).format();

    case FORMATS.CURRENCY_FRACTIONAL:
      return curr.value !== 0 ? currency(value, {
        precision: 3,
        format: fractionalFormatter
      }).format() : currency(value, {
        precision: 0
      }).format();

    case FORMATS.CURRENCY_WITH_CENTS:
      return curr.value !== 0 ? curr.format() : currency(value, {
        precision: 0
      }).format();

    case FORMATS.DECIMAL:
      return currency(value, {
        symbol: ''
      }).format();

    case FORMATS.NUMBER:
      return currency(value, {
        precision: 0,
        symbol: ''
      }).format();

    case FORMATS.PERCENT:
      return currency(value, {
        symbol: '%',
        pattern: '#!',
        precision: 0
      }).format();

    case FORMATS.PERCENT_WITH_DECIMAL:
      return currency(value, {
        symbol: '%',
        pattern: '#!'
      }).format();

    case FORMATS.STRING:
    default:
      return value;
  }
};
/**
 * Format a value to have a maximum of 2 decimals.
 * @param {string} value - The string value to be formatted.
 * @return {string} - Formatted string.
 */

export var formatTwoDecimals = function formatTwoDecimals(value) {
  return !_isNil(value) ? formatValue(value, FORMATS.DECIMAL) : 'N/A';
};
/**
 * Provided a min & max number, format it as a percentage range.
 *
 * @param {number} min - The minimum value
 * @param {number} max - The maximum value
 * @param {boolean} [respectZero=false] - Whether to respect zero as valid value
 *  @example
 *  loans verticals often require non-`null` values for fields like `min_variable_apr`
 *
 * @return {string} - The formatted percentage range
 */

export var getPercentageRange = function getPercentageRange(min, max) {
  var respectZero = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var parsedMin = parseFloat(min);
  var parsedMax = parseFloat(max);
  var hasMin = respectZero ? !Number.isNaN(parsedMin) : !!parsedMin;
  var hasMax = respectZero ? !Number.isNaN(parsedMax) : !!parsedMax;
  var formattedMin = formatTwoDecimals(parsedMin);
  var formattedMax = formatTwoDecimals(parsedMax);

  if (hasMin && hasMax) {
    return "".concat(formattedMin, "-").concat(formattedMax, "%");
  }

  if (hasMin) {
    return "".concat(formattedMin, "%");
  }

  if (hasMax) {
    return "".concat(formattedMax, "%");
  }

  return null;
};